import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  ArrowLeft, User, Zap, Wrench, CreditCard, Edit3,
  CheckCircle, Loader2, Trash2, AlertCircle, Plus,
  Truck, ExternalLink, Copy, Link2, Printer
} from "lucide-react";
import { LEGACY_PIPELINE, StatusBadge, getLegacyPipelineIdx } from "@/components/StatusPipeline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = d => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "";

const VOLTAGES = ["48V","54.6V","58.4V","60V","67.2V","69.3V","71.2V","72V","73V","84V","96V"];
const AMPERES = ["5A","6A","8A","10A","12A","15A","20A","25A","30A"];
const BODY_TYPES = ["Metal Body","Aluminium Body","Plastic Body"];
const WARRANTY_STATUSES = ["Under Warranty","Out of Warranty","Expired","Unknown"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 12 }, (_, i) => String(currentYear - i));
const EMPTY_CHARGER = { charger_type:"", charger_spec:"", brand_model:"", serial_number:"", body_type:"", warranty_status:"", mfg_month:"", mfg_year:"" };

const PAY_STYLES = {
  "Unpaid":  "bg-rose-50 text-rose-700 border-rose-200",
  "Partial": "bg-amber-50 text-amber-700 border-amber-200",
  "Paid":    "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const CardHeader = ({ icon: Icon, title, onEdit }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-xl bg-green-50 flex items-center justify-center">
        <Icon size={15} className="text-green-600" />
      </div>
      <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>{title}</h3>
    </div>
    {onEdit && (
      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-green-600" onClick={onEdit}>
        <Edit3 size={13} />
      </Button>
    )}
  </div>
);

export default function SCJobDetail() {
  const { scId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Dialog toggles
  const [editCustomer, setEditCustomer] = useState(false);
  const [editChargers, setEditChargers] = useState(false);
  const [editSpecs, setEditSpecs] = useState(false);
  const [editPayment, setEditPayment] = useState(false);
  const [editCourier, setEditCourier] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Form state
  const [custForm, setCustForm] = useState({});
  const [chargersForm, setChargersForm] = useState([]);
  const [specsForm, setSpecsForm] = useState({});
  const [payForm, setPayForm] = useState({});
  const [statusForm, setStatusForm] = useState({ status:"", notes:"" });
  const [courierForm, setCourierForm] = useState({ courier_name:"", awb_number:"", tracking_url:"" });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const r = await api.get(`${API}/sc/jobs/${scId}`);
        setJob(r.data);
      } catch { setError("SC Job not found or failed to load"); }
      finally { setLoading(false); }
    };
    load();
  }, [scId]);

  const save = async (field, data) => {
    setSaving(true);
    try {
      const r = await api.put(`${API}/sc/jobs/${job.id}`, { [field]: data });
      setJob(r.data);
      toast.success("Updated!");
    } catch { toast.error("Failed to update"); }
    finally { setSaving(false); }
  };

  const handleStatusUpdate = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`${API}/sc/jobs/${job.id}/status`, statusForm);
      setJob(r.data);
      toast.success(`Status updated to ${statusForm.status}`);
      setUpdateStatus(false);
    } catch { toast.error("Failed to update status"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${API}/sc/jobs/${job.id}`);
      toast.success("SC Job deleted");
      navigate("/sc");
    } catch { toast.error("Failed to delete SC job"); }
  };

  const handleSaveCourier = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`${API}/sc/jobs/${job.id}/courier`, courierForm);
      setJob(r.data);
      setEditCourier(false);
      toast.success("Courier details saved");
    } catch { toast.error("Failed to save courier details"); }
    finally { setSaving(false); }
  };

  const handleCopyTrackingLink = () => {
    if (!job) return;
    const link = `${window.location.origin}/sc-track?job=${job.sc_no}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Tracking link copied!"))
      .catch(() => window.open(link, "_blank"));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#f5faf6] flex items-center justify-center">
      <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-screen-2xl">
        <Skeleton className="h-10 w-64 mb-6" />
        <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#f5faf6] flex flex-col items-center justify-center py-24">
      <AlertCircle size={40} className="text-rose-400 mb-3" />
      <p className="text-slate-600 font-medium">{error}</p>
      <Button className="mt-4 bg-green-600 hover:bg-green-700 text-white" onClick={() => navigate("/sc")}>Back to SC Jobs</Button>
    </div>
  );

  const currentIdx = getLegacyPipelineIdx(job.current_status, job.status_history || []);
  const historyMap = Object.fromEntries((job.status_history || []).map(h => [h.status, h]));
  const nextStep = LEGACY_PIPELINE[currentIdx + 1];

  return (
    <div className="min-h-screen bg-[#f5faf6]">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200/80 shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => navigate("/sc")}>
            <ArrowLeft size={16} />
          </Button>
          <img src="/logo.jpg" alt="XSTRONG POWER" className="h-8 w-auto object-contain flex-shrink-0" />
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <span className="font-bold text-sm text-slate-900 font-mono truncate" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
              {job.sc_no}
            </span>
            <StatusBadge status={job.current_status} size="sm" />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {nextStep && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                onClick={() => { setStatusForm({ status: nextStep.status, notes:"" }); setUpdateStatus(true); }}
                data-testid="sc-advance-btn">
                <CheckCircle size={13} className="mr-1" /> {nextStep.status} →
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-400 hover:text-rose-600 hover:bg-rose-50"
              onClick={() => setDeleteConfirm(true)}>
              <Trash2 size={15} />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT — main info (2/3 width) */}
        <div className="lg:col-span-2 space-y-5">

          {/* Customer */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <CardHeader icon={User} title="Customer Details"
                onEdit={() => { setCustForm({ ...job.customer }); setEditCustomer(true); }} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["Customer Name", job.customer?.name],
                  ["Phone", job.customer?.phone],
                  ["Alternate Phone", job.customer?.alt_phone],
                  ["City", job.customer?.city],
                  ["State", job.customer?.state],
                  ["PIN Code", job.customer?.pincode],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label}>
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{val}</p>
                  </div>
                ))}
                {job.customer?.address && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-slate-400">Street Address</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{job.customer.address}</p>
                  </div>
                )}
                {job.customer?.landmark && (
                  <div className="sm:col-span-2">
                    <p className="text-xs text-slate-400">Landmark</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{job.customer.landmark}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Chargers */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <CardHeader icon={Zap} title={`Charger Details (${(job.chargers || []).length})`}
                onEdit={() => { setChargersForm(JSON.parse(JSON.stringify(job.chargers || [{ ...EMPTY_CHARGER }]))); setEditChargers(true); }} />
              {(job.chargers || []).length === 0 ? (
                <p className="text-xs text-slate-400 italic">No charger details added</p>
              ) : (
                <div className="space-y-4">
                  {(job.chargers).map((c, i) => (
                    <div key={i} className={i > 0 ? "pt-4 border-t border-slate-100" : ""}>
                      {job.chargers.length > 1 && <p className="text-xs font-semibold text-green-700 mb-2">Charger #{i + 1}</p>}
                      {/* Charger type + spec badge row */}
                      {(c.charger_type || c.charger_spec) && (
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          {c.charger_type && (
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                              {c.charger_type === "Small Charger" ? "🔋" : c.charger_type === "Fast Charger" ? "⚡" : "🛺"} {c.charger_type}
                            </span>
                          )}
                          {c.charger_spec && (
                            <span className="text-sm font-bold font-mono text-green-700 px-2.5 py-1 bg-green-50 rounded-lg border border-green-200">
                              {c.charger_spec}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          ["Brand / Model", c.brand_model],
                          ["Serial Number", c.serial_number],
                          ["Voltage", c.voltage],
                          ["Ampere", c.ampere],
                          ["Body Type", c.body_type],
                          ["Warranty", c.warranty_status],
                          ["Mfg Month", c.mfg_month ? MONTHS[parseInt(c.mfg_month) - 1] : ""],
                          ["Mfg Year", c.mfg_year],
                        ].filter(([, v]) => v).map(([label, val]) => (
                          <div key={label}>
                            <p className="text-xs text-slate-400">{label}</p>
                            <p className="text-sm font-medium text-slate-800 mt-0.5">{val}</p>
                          </div>
                        ))}
                      </div>
                      {/* Charger photo */}
                      {c.photo_url && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-400 mb-1.5">Photo</p>
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} target="_blank" rel="noreferrer">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt={"Charger #" + (i+1)}
                              className="w-32 h-32 object-cover rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow" />
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fault & Notes */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <CardHeader icon={Wrench} title="Fault & Notes"
                onEdit={() => { setSpecsForm({ ...job.specs }); setEditSpecs(true); }} />
              <div className="space-y-3">
                {[["Fault Description", job.specs?.fault_description],["Technical Notes", job.specs?.technical_notes],["Repair Notes", job.specs?.repair_notes]]
                  .map(([label, val]) => (
                    <div key={label}>
                      <p className="text-xs text-slate-400 mb-1">{label}</p>
                      <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg min-h-[36px]">
                        {val || <span className="text-slate-400 italic">None</span>}
                      </p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>

          {/* Courier Details */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Truck size={15} className="text-blue-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                    Courier Details
                  </h3>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-600 hover:bg-blue-50"
                  onClick={() => { setCourierForm({ courier_name: job.courier_details?.courier_name||"", awb_number: job.courier_details?.awb_number||"", tracking_url: job.courier_details?.tracking_url||"" }); setEditCourier(true); }}>
                  <Edit3 size={12} className="mr-1" /> {job.courier_details?.awb_number ? "Edit" : "Add"}
                </Button>
              </div>
              {job.courier_details?.awb_number ? (
                <div className="space-y-2">
                  {[["Courier Service", job.courier_details.courier_name], ["AWB / Tracking No.", job.courier_details.awb_number]].map(([l, v]) => v ? (
                    <div key={l} className="flex justify-between items-center py-1.5 border-b border-slate-50 last:border-0">
                      <span className="text-xs text-slate-400">{l}</span>
                      <span className={`text-sm font-semibold ${l.includes("AWB") ? "font-mono text-blue-700" : "text-slate-800"}`}>{v}</span>
                    </div>
                  ) : null)}
                  {job.courier_details.tracking_url && (
                    <a href={job.courier_details.tracking_url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 mt-2 p-2.5 bg-blue-50 rounded-xl border border-blue-200 font-medium">
                      <ExternalLink size={12} /> Track Shipment Online
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No courier details added yet</p>
              )}
            </CardContent>
          </Card>

          {/* 9-step Timeline */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                  Service Journey — Step {currentIdx + 1} of {LEGACY_PIPELINE.length}
                </h3>
                <span className="text-xs text-slate-400">{Math.round(((currentIdx + 1) / LEGACY_PIPELINE.length) * 100)}% complete</span>
              </div>

              {/* Progress bar */}
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">Pick Up Request</span>
                <span className="text-xs text-slate-400">Delivered</span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-5">
                <div className="h-full bg-green-500 rounded-full transition-all"
                  style={{ width: `${((currentIdx + 1) / LEGACY_PIPELINE.length) * 100}%` }} />
              </div>

              {/* Steps */}
              <div className="relative" data-testid="sc-status-timeline">
                <div className="absolute left-5 top-3 bottom-3 w-0.5 bg-slate-100" />
                <div className="space-y-0">
                  {LEGACY_PIPELINE.map((step, i) => {
                    const done    = i < currentIdx;
                    const current = i === currentIdx;
                    const future  = i > currentIdx;
                    const hist    = historyMap[step.status];
                    const Icon    = step.icon;
                    return (
                      <div key={step.status} className={`relative flex gap-4 pb-5 last:pb-0 ${future ? "opacity-40" : ""}`}>
                        <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${
                          current ? "border-green-500 bg-green-50 shadow-md" : done ? "border-green-300 bg-green-50" : "border-slate-200 bg-white"
                        }`}>
                          {current
                            ? <div className="relative">
                                <div className="absolute inset-0 rounded-full animate-ping opacity-25" style={{ background: step.dot }} />
                                <Icon size={16} style={{ color: step.dot }} className="relative z-10" />
                              </div>
                            : <Icon size={16} className={done ? "text-green-600" : "text-slate-300"} />}
                        </div>
                        <div className="flex-1 min-w-0 pt-1.5">
                          <div className="flex items-center justify-between flex-wrap gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-base">{step.emoji}</span>
                              <span className={`text-sm font-semibold ${current ? "text-green-700" : done ? "text-slate-800" : "text-slate-400"}`}>
                                {step.status}
                              </span>
                              {current && <span className="text-[10px] bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">Current</span>}
                            </div>
                            {hist && <span className="text-xs text-slate-400">{fmtDT(hist.timestamp)}</span>}
                          </div>
                          <p className={`text-xs mt-0.5 ${done || current ? "text-slate-500" : "text-slate-300"}`}>{step.desc}</p>
                          {hist?.notes && <p className="text-xs mt-1 text-slate-600 bg-slate-50 px-2.5 py-1.5 rounded-lg border border-slate-100">📝 {hist.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — payment + actions (1/3 width) */}
        <div className="space-y-5">
          {/* Payment */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <CardHeader icon={CreditCard} title="Payment"
                onEdit={() => { setPayForm({ ...job.payment }); setEditPayment(true); }} />
              <div className="space-y-2 mb-4">
                {[["Total", `₹${(job.payment?.amount||0).toLocaleString("en-IN")}`,"text-slate-900"],
                  ["Paid",  `₹${(job.payment?.paid_amount||0).toLocaleString("en-IN")}`,"text-emerald-700"]].map(([l,v,c]) => (
                  <div key={l} className="flex justify-between">
                    <span className="text-xs text-slate-500">{l}</span>
                    <span className={`text-sm font-semibold tabular-nums ${c}`}>{v}</span>
                  </div>
                ))}
                <Separator />
                <div className="flex justify-between">
                  <span className="text-xs font-semibold text-slate-600">Due</span>
                  <span className={`text-sm font-bold tabular-nums ${(job.payment?.due_amount||0) > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    ₹{(job.payment?.due_amount||0).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded-lg mb-3">
                <span className="text-xs text-slate-500">Method</span>
                <span className="text-xs font-medium">{job.payment?.method||"Cash"}</span>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${PAY_STYLES[job.payment?.payment_status]||"bg-slate-100"}`}>
                {job.payment?.payment_status||"Unpaid"}
              </span>
              {job.payment?.payment_status !== "Paid" && job.payment?.amount > 0 && (
                <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                  onClick={() => { setPayForm({...job.payment, paid_amount: job.payment?.amount||0}); setEditPayment(true); }}>
                  Mark as Fully Paid
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="rounded-2xl border border-slate-200/70 bg-white">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>Quick Actions</h3>
              <div className="space-y-2">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white text-xs h-8"
                  onClick={() => { setStatusForm({ status: job.current_status, notes:"" }); setUpdateStatus(true); }}
                  data-testid="sc-update-status-btn">
                  <CheckCircle size={13} className="mr-1.5" /> Update Status
                </Button>
                {/* Customer Tracking Link */}
                <Button variant="outline" className="w-full text-xs h-8 text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={handleCopyTrackingLink} data-testid="sc-copy-tracking-link">
                  <Copy size={12} className="mr-1.5" /> Copy Customer Tracking Link
                </Button>
                <a href={`/sc-track?job=${job.sc_no}`} target="_blank" rel="noreferrer" className="block">
                  <Button variant="outline" className="w-full text-xs h-8 text-green-700 border-green-200 hover:bg-green-50">
                    <Link2 size={12} className="mr-1.5" /> Open Tracking Page
                  </Button>
                </a>
                <a href={`/sc-print/${job.id}`} target="_blank" rel="noreferrer" className="block">
                  <Button variant="outline" className="w-full text-xs h-8 text-slate-700 border-slate-200 hover:bg-slate-50">
                    <Printer size={12} className="mr-1.5" /> Print Job Card
                  </Button>
                </a>
                <Button variant="outline" className="w-full text-xs h-8 text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => setDeleteConfirm(true)}>
                  <Trash2 size={13} className="mr-1.5" /> Delete Job
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ===== DIALOGS ===== */}

      {/* Update Status Dialog */}
      <Dialog open={updateStatus} onOpenChange={setUpdateStatus}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update Service Status</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium">New Status</Label>
              <Select value={statusForm.status} onValueChange={v => setStatusForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEGACY_PIPELINE.filter((s, i, arr) => arr.findIndex(x => x.status === s.status) === i).map(s => <SelectItem key={s.status} value={s.status}>{s.emoji} {s.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">Notes (optional)</Label>
              <Textarea className="mt-1.5 resize-none text-sm" rows={2}
                placeholder="Add a note about this update..."
                value={statusForm.notes} onChange={e => setStatusForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateStatus(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleStatusUpdate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editCustomer} onOpenChange={setEditCustomer}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Customer Details</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
            {[["name","Full Name"],["phone","Phone Number"],["alt_phone","Alternate Phone"],
              ["address","Street Address"],["landmark","Landmark"],
              ["city","City"],["pincode","PIN Code"]].map(([k,l]) => (
              <div key={k}>
                <Label className="text-xs font-medium">{l}</Label>
                <Input className="mt-1.5 h-9 text-sm" value={custForm[k]||""}
                  onChange={e => setCustForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <div>
              <Label className="text-xs font-medium">State</Label>
              <Select value={custForm.state||""} onValueChange={v => setCustForm(f => ({ ...f, state: v }))}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent className="max-h-52">
                  {["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jammu and Kashmir","Jharkhand","Karnataka","Kerala","Ladakh","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Chandigarh"].map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCustomer(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={saving}
              onClick={() => { save("customer", custForm); setEditCustomer(false); }}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Chargers Dialog */}
      <Dialog open={editChargers} onOpenChange={setEditChargers}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Charger Details</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {chargersForm.map((c, i) => (
              <div key={i} className="p-4 border border-slate-200 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-green-700">Charger #{i + 1}</p>
                  {chargersForm.length > 1 && (
                    <button type="button" onClick={() => setChargersForm(p => p.filter((_, idx) => idx !== i))}
                      className="text-rose-400 hover:text-rose-600 p-1 rounded"><Trash2 size={13} /></button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[["brand_model","Brand / Model","text"],["serial_number","Serial Number","text"]].map(([k,l]) => (
                    <div key={k}>
                      <Label className="text-xs">{l}</Label>
                      <Input className="mt-1 h-9 text-sm" value={c[k]||""}
                        onChange={e => setChargersForm(p => { const n=[...p]; n[i]={...n[i],[k]:e.target.value}; return n; })} />
                    </div>
                  ))}
                  {[
                    ["voltage","Voltage",VOLTAGES],
                    ["ampere","Ampere",AMPERES],
                    ["body_type","Body Type",BODY_TYPES],
                    ["warranty_status","Warranty Status",WARRANTY_STATUSES],
                  ].map(([k,l,opts]) => (
                    <div key={k}>
                      <Label className="text-xs">{l}</Label>
                      <Select value={c[k]||""} onValueChange={v => setChargersForm(p => { const n=[...p]; n[i]={...n[i],[k]:v}; return n; })}>
                        <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder={`Select ${l.toLowerCase()}`} /></SelectTrigger>
                        <SelectContent>{opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs">Mfg Month</Label>
                    <Select value={c.mfg_month||""} onValueChange={v => setChargersForm(p => { const n=[...p]; n[i]={...n[i],mfg_month:v}; return n; })}>
                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent className="max-h-52">
                        {["1","2","3","4","5","6","7","8","9","10","11","12"].map((m,idx) => <SelectItem key={m} value={m}>{MONTHS[idx]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Mfg Year</Label>
                    <Select value={c.mfg_year||""} onValueChange={v => setChargersForm(p => { const n=[...p]; n[i]={...n[i],mfg_year:v}; return n; })}>
                      <SelectTrigger className="mt-1 h-9 text-sm"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent className="max-h-52">{YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" onClick={() => setChargersForm(p => [...p, { ...EMPTY_CHARGER }])}
              className="w-full flex items-center justify-center gap-2 text-sm text-green-700 border border-dashed border-green-300 rounded-xl py-2.5 hover:bg-green-50 transition-colors">
              <Plus size={14} /> Add Charger
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditChargers(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={saving}
              onClick={() => { save("chargers", chargersForm); setEditChargers(false); }}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Specs Dialog */}
      <Dialog open={editSpecs} onOpenChange={setEditSpecs}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Fault & Notes</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {[["fault_description","Fault Description"],["technical_notes","Technical Notes"],["repair_notes","Repair Notes"]].map(([k,l]) => (
              <div key={k}>
                <Label className="text-xs font-medium">{l}</Label>
                <Textarea className="mt-1.5 resize-none text-sm" rows={2} value={specsForm[k]||""}
                  onChange={e => setSpecsForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSpecs(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={saving}
              onClick={() => { save("specs", specsForm); setEditSpecs(false); }}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editPayment} onOpenChange={setEditPayment}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Payment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              {[["amount","Total (₹)"],["paid_amount","Amount Paid (₹)"]].map(([k,l]) => (
                <div key={k}>
                  <Label className="text-xs font-medium">{l}</Label>
                  <Input type="number" className="mt-1.5 h-9 text-sm" value={payForm[k]||""}
                    onChange={e => setPayForm(f => ({ ...f, [k]: parseFloat(e.target.value)||0 }))} />
                </div>
              ))}
            </div>
            <div>
              <Label className="text-xs font-medium">Payment Method</Label>
              <Select value={payForm.method||"Cash"} onValueChange={v => setPayForm(f => ({ ...f, method: v }))}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{["Cash","UPI","Card","Bank Transfer"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPayment(false)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" disabled={saving}
              onClick={() => { save("payment", payForm); setEditPayment(false); }}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Courier Dialog */}
      <Dialog open={editCourier} onOpenChange={setEditCourier}>
        <DialogContent>
          <DialogHeader><DialogTitle>Courier Details</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            {[["courier_name","Courier Name","e.g. Ekart, Amazon, Delhivery"],
              ["awb_number","AWB / Tracking Number","e.g. 371658740311"],
              ["tracking_url","Tracking Link (URL)","https://track.courier.com/..."]].map(([k,l,ph]) => (
              <div key={k}>
                <Label className="text-xs font-medium">{l}</Label>
                <Input className="mt-1.5 h-9 text-sm" placeholder={ph} value={courierForm[k]||""}
                  onChange={e => setCourierForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCourier(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving} onClick={handleSaveCourier}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save Courier Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SC Job {job?.sc_no}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The job will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
